import { useAuth0 } from '@auth0/auth0-react';
import type { User, UserRole } from '@fig/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: Error | undefined;
  login: (returnTo?: string) => void;
  logout: () => void;
  getAccessToken: () => Promise<string>;
}

export function useAuth(): AuthState {
  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();
  const [resolvedUser, setResolvedUser] = useState<User | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleError, setRoleError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !auth0User) {
      setResolvedUser(null);
      setRoleLoading(false);
      setRoleError(undefined);
      return;
    }

    setRoleLoading(true);
    setRoleError(undefined);

    const resolveRole = async () => {
      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch {
        // The session/token has expired and could not be refreshed silently.
        // Redirect to login rather than falling through to the Unauthorized page.
        if (!cancelled) {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        // The token is expired or otherwise invalid on the server. This is an
        // authentication failure (not an authorization one), so send the user
        // back through login instead of showing the Unauthorized page.
        if (!cancelled) {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        return;
      }

      if (!response.ok) {
        throw new Error(`Unable to resolve application role (${response.status})`);
      }

      const user = (await response.json()) as {
        id: string;
        email: string;
        role: UserRole;
        fullName?: string | null;
        salesTeams?: Array<{ salesTeamId: string; salesTeamName: string }>;
      };

      if (cancelled) return;

      const salesTeams = user.salesTeams ?? [];
      const salesTeamNames = salesTeams.map((team) => team.salesTeamName);

      setResolvedUser({
        id: user.id,
        email: user.email,
        role: user.role,
        name: user.fullName ?? auth0User.name,
        salesTeam: salesTeamNames.length > 0 ? salesTeamNames.join(', ') : undefined,
        salesTeams: salesTeams.length > 0 ? salesTeams : undefined,
      });
    };

    resolveRole()
      .catch((err) => {
        if (cancelled) return;

        setResolvedUser(null);
        setRoleError(err instanceof Error ? err : new Error('Unable to resolve application role'));
      })
      .finally(() => {
        if (!cancelled) {
          setRoleLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth0User, getAccessTokenSilently, isAuthenticated]);

  const user: User | null = useMemo(() => {
    if (!auth0User) return null;
    return resolvedUser;
  }, [auth0User, resolvedUser]);
  const isResolvingRole = Boolean(isAuthenticated && auth0User && !resolvedUser && !roleError);

  const login = useCallback(
    (returnTo?: string) => {
      loginWithRedirect({
        appState: {
          returnTo: returnTo ?? `${window.location.pathname}${window.location.search}`,
        },
      });
    },
    [loginWithRedirect]
  );

  const logout = useCallback(() => {
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [auth0Logout]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    try {
      return await getAccessTokenSilently();
    } catch {
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}`,
        },
      });
      throw new Error('Token refresh failed. Redirecting to login.');
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  return {
    isAuthenticated,
    isLoading: isLoading || roleLoading || isResolvingRole,
    user,
    error: error ?? roleError,
    login,
    logout,
    getAccessToken,
  };
}
